import { useState } from "react";
import { toast } from "sonner";
import { pu, eur } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Save, PlusCircle } from "lucide-react";

const CONSIGLIATE = {
  Pizzeria: [["Cotto/Salame", 1], ["Verdure", 0.5], ["Mozzarella", 1], ["Bufala", 1.5],
    ["Affettati (crudo/speck)", 1.5], ["Bresaola", 2], ["Calamari", 2], ["Patatine", 1],
    ["Formaggi", 1], ["No glutine", 5], ["Burrata", 4.5], ["Salmone/Gamberi", 1.5],
    ["Panna pizza", 1.5]],
  Cucina: [["Bacon", 1], ["Uovo", 1], ["Cheddar", 1], ["Cipolla", 1], ["Insalata", 1],
    ["Pomodoro", 1], ["Contorno extra", 1], ["Grana", 1]],
  Bar: [["Ghiaccio", 0], ["Limone", 0], ["Sciroppo", 0.5]],
};

/**
 * Le aggiunte si impostano una volta per reparto e la sala le trova su ogni piatto
 * di quel reparto quando prende l'ordine.
 */
export const AggiunteReparto = ({ departments = [] }) => {
  const { settings, refresh } = useApp();
  const reparti = departments.filter((d) => CONSIGLIATE[d]);
  const [dept, setDept] = useState(reparti[0] || "Cucina");
  const [righe, setRighe] = useState(() => {
    const vecchie = (settings?.extras_pizzeria || []).map((e) => ({ ...e, dept: "Pizzeria" }));
    return [...vecchie, ...(settings?.extras_locale || [])];
  });
  const [nome, setNome] = useState("");
  const [prezzo, setPrezzo] = useState("1,50");
  const [busy, setBusy] = useState(false);

  const delReparto = righe.filter((r) => (r.dept || "Cucina") === dept);

  const salva = async (next) => {
    setBusy(true);
    try {
      await pu("/settings", { ...settings, extras_pizzeria: [], extras_locale: next });
      setRighe(next);
      toast.success("Aggiunte salvate: la sala le vede su tutti i piatti del reparto");
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiunte non salvate");
    } finally {
      setBusy(false);
    }
  };

  const aggiungi = () => {
    const n = nome.trim();
    if (!n) return;
    if (delReparto.some((r) => r.name.toLowerCase() === n.toLowerCase())) return toast.error("Aggiunta già presente");
    setRighe([...righe, { name: n, amount: Number(String(prezzo).replace(",", ".")) || 0, dept }]);
    setNome("");
  };

  const cambia = (r, amount) =>
    setRighe(righe.map((x) => (x === r ? { ...x, amount } : x)));

  return (
    <Card className="space-y-3 p-5" data-testid="aggiunte-reparto">
      <div>
        <h2 className="font-head text-base font-bold">Aggiunte per reparto</h2>
        <p className="mt-1 text-xs text-slate-500">
          Valgono per tutti i piatti del reparto: la sala le trova toccando la freccia del piatto e
          finiscono nel conto. Chi prende l'ordine può anche scrivere un'aggiunta libera al momento.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {reparti.map((d) => (
          <button
            key={d}
            data-testid={`aggiunte-tab-${d.toLowerCase()}`}
            onClick={() => setDept(d)}
            className={`h-9 rounded-lg px-3 text-sm font-bold ${
              d === dept ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
            }`}
          >
            {d}
          </button>
        ))}
      </div>

      {delReparto.length === 0 ? (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">Nessuna aggiunta per {dept}.</p>
          <Button size="sm" variant="outline" disabled={busy} data-testid="aggiunte-consigliate"
                  onClick={() => salva([...righe, ...CONSIGLIATE[dept].map(([n, a]) => ({ name: n, amount: a, dept }))])}>
            <PlusCircle className="mr-1.5 h-3.5 w-3.5" /> Usa quelle consigliate per {dept}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
        <Button size="sm" variant="outline" disabled={busy} data-testid="aggiunte-sostituisci"
                onClick={() => salva([...righe.filter((r) => (r.dept || "Cucina") !== dept),
                                      ...CONSIGLIATE[dept].map(([n, a]) => ({ name: n, amount: a, dept }))])}>
          <PlusCircle className="mr-1.5 h-3.5 w-3.5" /> Rimetti il listino consigliato per {dept}
        </Button>
        <div className="flex flex-wrap gap-2" data-testid="aggiunte-lista">
          {delReparto.map((r, idx) => (
            <span key={`${r.dept}-${r.name}`} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-xs font-semibold text-slate-700">
              {r.name}
              <Input
                className="h-7 w-16 border-0 bg-white px-1.5 text-center text-xs"
                value={String(r.amount).replace(".", ",")}
                data-testid={`aggiunta-prezzo-${idx}`}
                onChange={(e) => cambia(r, Number(e.target.value.replace(",", ".")) || 0)}
              />
              <button className="rounded-full p-1 text-rose-600" data-testid={`aggiunta-togli-${idx}`}
                      onClick={() => setRighe(righe.filter((x) => x !== r))}>
                <Trash2 className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-10 w-44" placeholder={`nuova aggiunta ${dept}`} value={nome}
               onChange={(e) => setNome(e.target.value)} data-testid="aggiunta-nome" />
        <Input className="h-10 w-20 text-center" placeholder="1,50" value={prezzo}
               onChange={(e) => setPrezzo(e.target.value)} data-testid="aggiunta-importo" />
        <Button size="sm" variant="outline" onClick={aggiungi} data-testid="aggiunta-add">
          <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi
        </Button>
        <Button size="sm" disabled={busy} onClick={() => salva(righe)} data-testid="aggiunte-salva">
          <Save className="mr-1 h-3.5 w-3.5" /> Salva
        </Button>
        {delReparto.length > 0 && (
          <span className="font-num text-[11px] text-slate-500">
            {delReparto.length} aggiunte · da {eur(Math.min(...delReparto.map((r) => r.amount)))} a {eur(Math.max(...delReparto.map((r) => r.amount)))}
          </span>
        )}
      </div>
    </Card>
  );
};
